import React, { useState } from "react";
import { Form, Row, Button, Col } from "react-bootstrap";
import { Formik } from "formik";
import { ActionHandlerFunction } from "@preveil-api";
import {
  findInvalidAddress, findDuplicateAddress, AdminUIActionTypes, EmailGatewayEntity, RegexHelper, EmailGatewayType, CSVUploadStatusHeading, useAppSelector, usePostUsersFindMutation, Account,
  AdminErrorMessages, MessageAnchors
} from "src/common";
import { Icon } from "src/components";
import { AddressPanel } from "..";
import { RootState } from "src/store/configureStore";
import _ from "lodash";
import * as yup from "yup";

type AllProps = {
  saved_address_list?: string[];
  submitAddresses: Function;
  handleAction: ActionHandlerFunction;
}

function IndividualFormComponent(props: AllProps) {
  const { saved_address_list, handleAction, submitAddresses } = props;
  const current_account = useAppSelector((state: RootState) => state.account.current_account);
  const [is_form_valid, setIsFormValid] = useState<boolean>(false);
  const [address_list, setAddressList] = useState<EmailGatewayEntity[]>([]);
  const [findUsers] = usePostUsersFindMutation();
  const schema = yup.object().shape({
    alias: yup
      .string()
      .required(AdminErrorMessages.error_alias_required)
      .test("alias-field", AdminErrorMessages.error_alias_format, (value) => {
        if (value) {
          return findInvalidAddress([value.trim()]).length === 0;
        }
        return !!value;
      })
  });

  function handleDeleteAction(id?: string) {
    const filtered_list = address_list.filter((address) => address.email_address !== id);
    setAddressList(filtered_list);
  };

  function handleSubmit(e: React.FormEvent<HTMLFormElement | HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    submitAddresses(address_list);
  }

  return (
    <Formik
      validationSchema={schema}
      onSubmit={() => { }}
      initialValues={{
        alias: "",
        name: "",
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, resetForm, setFieldError, setFieldValue }) => {

        // Description: handles alias input form validation errors
        function handleAliasErrors(message: string) {
          setFieldError('alias', message);
          setFieldValue('alias', '', false);
          setFieldValue('name', '', false);
          setIsFormValid(false);
        }

        // Description: Handles and Validates user input address/domain
        // If address is a duplicate (either from saved data or already present in the array) it will warn the user.
        // If address is a PreVeil account, it will warn the user.
        function handleAddAddress(_e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
          const alias = values.alias.trim();
          const name = !!values.name ? values.name.trim() : "";
          const current_addresses = _.map(address_list, "email_address");
          const total_addresses = saved_address_list && saved_address_list.length >= 1 ? [...saved_address_list, ...current_addresses] : current_addresses;
          const is_duplicate_found = findDuplicateAddress(total_addresses, [alias]);

          // NOTE: Handle errors:
          if (is_duplicate_found) {
            handleAliasErrors(AdminErrorMessages.error_adding_duplicate_alias.replace(MessageAnchors.user_id, alias));
          } else {
            findUsers({
              account_ids: Account.getAccountIdentifiers(current_account as Account),
              body: { spec: [{ user_id: alias, key_version: -1 }] }
            }).unwrap()
              .then((pv_users) => {
                const is_preveil_account = pv_users.users?.length > 0;
                if (is_preveil_account) {
                  handleAliasErrors(AdminErrorMessages.error_adding_pv_alias);
                } else {
                  const email_address = alias.toLowerCase();
                  const display_name = name || email_address;
                  const type = RegexHelper.testDomain(email_address) ? "domain" : "individual" as EmailGatewayType;
                  const gateway_address = { email_address, display_name, type };
                  setAddressList(prev_array => [...prev_array, gateway_address]);
                  setIsFormValid(true);
                  resetForm();
                }
              }).catch((stack: unknown) => {
                handleAction({ actionType: AdminUIActionTypes.PageError, params: { message: AdminErrorMessages.error_validating_alias, stack } });
                resetForm();
              });
          }
        };

        return <Form noValidate className="mx-1">
          <div className="mb-4">
            <Row className="align-items-baseline g-2">
              <Form.Group as={Col} xs="6">
                <Form.Label>
                  <p className="m-0">
                    Address/Domain
                  </p>
                </Form.Label>
                <Form.Control
                  id="alias_tb"
                  type="text"
                  name="alias-field"
                  onChange={handleChange("alias")}
                  value={values.alias}
                  onBlur={handleBlur("alias")}
                  isInvalid={touched.alias && !!errors.alias}
                />
              </Form.Group>
              <Form.Group as={Col} xs="5">
                <Form.Label htmlFor="name_tb">
                  Name <span className="text-muted small">(optional)</span>
                </Form.Label>
                <Form.Control
                  id="name_tb"
                  type="text"
                  name="name-field"
                  onChange={handleChange("name")}
                  value={values.name}
                />
              </Form.Group>
              <Form.Group as={Col} xs="1" >
                <Form.Label className="opacity-0 mt-1">add</Form.Label>
                <Button
                  id="add_alias_btn"
                  variant="secondary"
                  onClick={handleAddAddress}
                  className="btn-icon font-size-sm"
                  disabled={!!errors.alias || !touched.alias} >
                  <Icon className="icon-plus2" />
                </Button>
              </Form.Group>
            </Row>
            {
              <Form.Control.Feedback type="invalid" className={`${touched.alias && !!errors.alias ? "d-block" : ""}`}>
                {errors.alias}
              </Form.Control.Feedback>
            }
          </div>
          {
            address_list.length !== 0 &&
            <AddressPanel
              title={CSVUploadStatusHeading.valid_gateway}
              address_list={address_list}
              handleDelete={handleDeleteAction} />
          }
          <footer className="btn-panel mt-1">
            <Button
              id="submit_btn"
              type="submit"
              className="me-2"
              disabled={!is_form_valid || address_list.length === 0}
              onClick={(e) => {
                handleSubmit(e);
                resetForm();
              }}
            >
              Add
            </Button>
            <Button
              id="cancel_form_btn"
              onClick={() => handleAction({ actionType: AdminUIActionTypes.ShowModal })}
              variant="no-outline-primary">Cancel</Button>
          </footer>
        </Form>;
      }}
    </Formik>
  );
}

export default React.memo(IndividualFormComponent);
